'use client';

import { LoaderCircle } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { Button } from '@upzi/web-shell/ui/button';

const LOAD_MORE_PREFETCH_DISTANCE_PX = 640;

type JobInfiniteScrollTriggerProps = {
  errorMessage?: string;
  hasMore: boolean;
  isLoading: boolean;
  onLoadMore: () => void;
};

export function JobInfiniteScrollTrigger({
  errorMessage,
  hasMore,
  isLoading,
  onLoadMore,
}: JobInfiniteScrollTriggerProps) {
  const triggerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const trigger = triggerRef.current;

    if (!trigger || !hasMore || isLoading || errorMessage) return;

    const observer = new IntersectionObserver(
      entries => {
        if (!entries[0]?.isIntersecting) return;

        observer.unobserve(trigger);
        onLoadMore();
      },
      {
        rootMargin: `0px 0px ${LOAD_MORE_PREFETCH_DISTANCE_PX}px 0px`,
      }
    );

    observer.observe(trigger);

    return () => observer.disconnect();
  }, [errorMessage, hasMore, isLoading, onLoadMore]);

  if (!hasMore && !isLoading && !errorMessage) return null;

  return (
    <div
      ref={triggerRef}
      aria-busy={isLoading}
      aria-live="polite"
      className="flex min-h-16 items-center justify-center py-4 [overflow-anchor:none]"
    >
      {isLoading ? (
        <span className="text-on-surface-variant flex items-center gap-2 text-sm">
          <LoaderCircle
            className="text-primary size-5 animate-spin"
            aria-hidden
          />
          Đang tải thêm việc làm…
        </span>
      ) : null}

      {errorMessage ? (
        <div className="flex flex-col items-center gap-2 text-center">
          <p className="text-negative text-sm">{errorMessage}</p>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onLoadMore}
          >
            Thử lại
          </Button>
        </div>
      ) : null}
    </div>
  );
}
